import { checkHasMoreItem, isDefine } from 'helpers';
import { showError } from 'helpers/handleError';
import { useEffect, useState, useCallback, useRef } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { flatten, isArray } from 'lodash';
import PromiseHandler from 'helpers/promiseHandler';
import CreativeServices from 'services/CreativeServices';
import CreativeModel from 'models/creative.model';
import { useSave } from 'redux/store/StoreCached';

//* GUIDE:
//**************************************
//* Check services of API              *
//* Check parseBodyToRequest function  *
//* Check checkConditionPass function  *
//**************************************

/**
 * @returns {{
 *  data: {"id":1511,"userId":708,"title":"string","message":"string","minLength":170,"maxLength":177,"generatedCase":3,"updated":"2023-11-24T08:43:20.784+00:00","sent":0,"clicked":0,"convertor":0,"replied":0,"callback":0,"listen":0,"routeId":null,"approvalStatus":"PENDING","rejectReason":null}[],
 *  loading: boolean,
 *  refetching: boolean,
 *  loadingMore: boolean,
 *  hasMore: boolean,
 *  error: string | any,
 *  refetch: () => Promise<void>,
 *  refetchWithLoading: () => Promise<void>,
 *  setData: Dispatch<SetStateAction<any[]>>,
 * }}
 */
const useGetListCreatives = (
  filters,
  { isTrigger = true, refetchKey = '' } = { isTrigger: true }
) => {
  //! State
  const save = useSave();
  const promiseHandler = useRef(new PromiseHandler());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  //! Function
  const parseBodyToRequest = useCallback((filters) => {
    return CreativeModel.parseRequestSearchCreatives(filters);
  }, []);

  const fetch = useCallback(() => {
    if (!isTrigger) {
      return;
    }

    return new Promise(async (resolve, reject) => {
      try {
        const response = await promiseHandler.current.takeLatest(
          CreativeServices.getList(parseBodyToRequest(filters))
        );
        resolve(response);
      } catch (error) {
        setError(error);
        reject(error);
      }
    });
  }, [filters, isTrigger, parseBodyToRequest]);

  const checkConditionPass = useCallback(
    (response, { isLoadmore } = {}) => {
      if (isArray(response?.data?.data)) {
        //* Check condition of response here to set data
        if (isLoadmore) {
          setData((prev) => {
            let nextPages = cloneDeep(prev);
            nextPages = [...(nextPages || []), ...response?.data?.data];
            return nextPages;
          });
        } else {
          setData(response?.data?.data);
        }

        const limit =
          (isDefine(filters.size) && filters.size) || (isDefine(filters.limit) && filters.limit);
        setHasMore(checkHasMoreItem(response?.data?.data, limit));
      }
    },
    [filters.size, filters.limit]
  );

  //* Refetch implicity (without changing loading state)
  const refetch = useCallback(async () => {
    try {
      setRefetching(true);
      //* Default for API that use PAGE + SIZE
      const isOffsetLimit = 'offset' in filters && 'limit' in filters;
      let page = filters.page || 0;

      //* Using in case if API that use LIMIT + OFFSET
      if (isOffsetLimit) {
        page = Math.ceil(filters.offset / filters.limit) + 1;
      }

      const listRequest = [];
      for (let eachPage = 0; isOffsetLimit ? eachPage < page : eachPage <= page; eachPage++) {
        const nextFilters = { ...filters };
        if (isOffsetLimit) {
          nextFilters.offset = eachPage * filters.limit;
        } else {
          nextFilters.page = eachPage;
        }

        const request = await CreativeServices.getList(parseBodyToRequest(nextFilters));
        listRequest.push(request);
      }

      const responses = await promiseHandler.current.takeLatest(Promise.allSettled(listRequest));
      const allData = responses.map((el) =>
        isArray(el?.value?.data?.data) ? el?.value?.data?.data : []
      );
      setData(flatten(allData));
    } catch (error) {
      if (!!error?.isCanceled) {
        return;
      }

      showError(error);
    } finally {
      setRefetching(false);
    }
  }, [filters, parseBodyToRequest]);

  //* Refetch with changing loading state
  const refetchWithLoading = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch();
      checkConditionPass(response);
    } catch (error) {
      if (!!error?.isCanceled) {
        return;
      }
      showError(error);
    } finally {
      setLoading(false);
    }
  }, [fetch, checkConditionPass]);

  useEffect(() => {
    if (!!refetchKey) {
      save(refetchKey, refetch);
    }
  }, [refetchKey, save, refetch]);

  useEffect(() => {
    let shouldSetData = true;

    //* Fecth API in UseEffect
    const fetchAPI = async () => {
      try {
        setLoading(true);
        const response = await fetch();
        if (shouldSetData) {
          checkConditionPass(response);
          setLoading(false);
        }
      } catch (error) {
        showError(error);
        setLoading(false);
      }
    };

    //* Fetch More in UseEffect
    const fetchMore = async () => {
      try {
        setLoadingMore(true);
        const response = await fetch();
        if (shouldSetData) {
          checkConditionPass(response, { isLoadmore: true });
          setLoadingMore(false);
        }
      } catch (error) {
        showError(error);
        setLoadingMore(false);
      }
    };

    if (
      (isDefine(filters.offset) && filters.offset <= 0) ||
      (isDefine(filters.page) && filters.page <= 0)
    ) {
      fetchAPI();
    } else {
      //* If offset > 0 -> fetch more
      fetchMore();
    }

    return () => {
      promiseHandler.current.cancel();
      shouldSetData = false;
    };
  }, [filters.page, filters.offset, fetch, checkConditionPass]);

  return {
    data,
    loading,
    error,
    refetch,
    refetchWithLoading,
    refetching,
    loadingMore,
    hasMore,
    setData,
  };
};

export default useGetListCreatives;
